import type { OptionGroup } from './listings.ts';

// Variants (DECISIONS, 23 October): one row per combination of the listing's
// options (groups of kind "option": buyers pick exactly one, "Colour: Red,
// Size: Large"), each with its own stock (or no limit), an optional price and
// an optional photo. An optional limit (`stockLimit`, 30 plates a day) caps
// every sale on top. A listing without options has one default row. Extras
// (toppings, gift box) are not part of a variant: they have an extra price.
// The rows are stored in the product document (`variantRows`), so checkout
// takes stock from a row in the same conditional write as the rest.

export const MAX_VARIANT_GROUPS = 3;
export const MAX_VARIANTS = 20;
export const DEFAULT_VARIANT_ID = 'default';
const MAX_STOCK = 1_000_000;
// The live app's `quantity` for a listing nothing caps (`stockUnlimited`): a
// placeholder so the live app keeps selling it. It is never stock: the app
// and the Worker go by the flag. Temporary until the live app is retired
// (DECISIONS, 23 October).
export const LEGACY_UNLIMITED_QUANTITY = MAX_STOCK;
const MAX_PRICE_MINOR = 100_000_000;

export type VariantOptions = Record<string, string>;
// stock null: no limit of its own (the listing's stock applies). priceMinor
// null: the listing's price. photo null: the photo of one of its options,
// else the listing's first photo (variantPhoto).
export type VariantRow = { id: string; options: VariantOptions; stock: number | null; priceMinor: number | null; photo: string | null };

// The groups whose combinations are variants: options. Groups saved before
// kinds existed count as options when they are required with one choice.
export function variantGroups(groups: OptionGroup[]): OptionGroup[] {
  return groups.filter(group => (group.kind ? group.kind === 'option' : group.required && !group.multi));
}

// Every combination, first group slowest: Red/S, Red/L, Blue/S, Blue/L.
// Empty when there are more than MAX_VARIANTS.
export function variantCombos(groups: { name: string; options: { name: string }[] }[]): VariantOptions[] {
  if (groups.reduce((count, group) => count * group.options.length, 1) > MAX_VARIANTS) return [];
  return groups.reduce<VariantOptions[]>((combos, group) => combos.flatMap(combo => group.options.map(option => ({ ...combo, [group.name]: option.name }))), [{}]);
}

// A row's id comes from its options, so it stays the same while the seller
// changes stock, prices or photos (and a cart keeps pointing at it).
export function variantId(options: VariantOptions, groups: Pick<OptionGroup, 'name'>[]): string {
  if (!groups.length) return DEFAULT_VARIANT_ID;
  // FNV-1a: a short, stable label, not a security measure.
  let hash = 0x811c9dc5;
  for (const char of JSON.stringify(groups.map(group => [group.name, options[group.name] ?? '']))) {
    hash ^= char.codePointAt(0)!;
    hash = Math.imul(hash, 0x01000193) >>> 0;
  }
  return `v${hash.toString(16).padStart(8, '0')}`;
}

// "Red / Large", or "" for the default row.
export function variantLabel(options: VariantOptions, groups: Pick<OptionGroup, 'name'>[]): string {
  return groups.map(group => options[group.name]).filter(Boolean).join(' / ');
}

function wholeNumber(value: unknown, min: number, max: number): number | null {
  const number = Number(value);
  return value !== '' && value !== null && value !== undefined && Number.isInteger(number) && number >= min && number <= max ? number : null;
}

const sameOptions = (a: VariantOptions, b: VariantOptions, groups: Pick<OptionGroup, 'name'>[]) => groups.every(group => a[group.name] === b[group.name]);

export type VariantParse = { ok: true; value: VariantRow[] } | { ok: false; message: string };

// The rows the seller sent, one for every combination: an empty stock is no
// limit, combinations not sent start at 0, rows for removed options are dropped.
export function parseVariantRows(raw: unknown, groups: OptionGroup[], photos: string[]): VariantParse {
  const vary = variantGroups(groups);
  if (vary.length > MAX_VARIANT_GROUPS) return { ok: false, message: `Use up to ${MAX_VARIANT_GROUPS} required options with one choice each, for example Colour, Size and Material.` };
  const combos = variantCombos(vary);
  if (!combos.length) return { ok: false, message: `Use up to ${MAX_VARIANTS} combinations. Remove a few choices.` };
  const sent = Array.isArray(raw) ? raw.filter(row => row && typeof row === 'object') as Record<string, unknown>[] : [];
  const rows: VariantRow[] = [];
  for (const options of combos) {
    const row = sent.find(item => item.options && typeof item.options === 'object' && sameOptions(item.options as VariantOptions, options, vary)) ?? { stock: 0 };
    const label = variantLabel(options, vary) || 'this listing';
    const unlimited = row.stock === undefined || row.stock === '' || row.stock === null;
    const stock = unlimited ? null : wholeNumber(row.stock, 0, MAX_STOCK);
    if (!unlimited && stock === null) return { ok: false, message: `Check the stock for ${label}.` };
    const priceMinor = row.priceMinor === undefined || row.priceMinor === '' || row.priceMinor === null ? null : wholeNumber(row.priceMinor, 1, MAX_PRICE_MINOR);
    if (priceMinor === null && row.priceMinor !== undefined && row.priceMinor !== '' && row.priceMinor !== null) return { ok: false, message: `Check the price for ${label}.` };
    const photo = row.photo ? String(row.photo) : null;
    if (photo !== null && !photos.includes(photo)) return { ok: false, message: `Choose the photo for ${label} again.` };
    rows.push({ id: variantId(options, vary), options, stock, priceMinor, photo });
  }
  return { ok: true, value: rows };
}

// Rows for a listing saved without them (the live app, or before a seller
// sets stock per combination). Exact only when there is nothing to guess:
// no variant groups, one group (each choice's stock, or no limit), or several
// groups where no choice has a stock of its own (every row without a limit;
// the listing's stock caps them). A row's price is the listing's price plus
// its choices' extras, as the live app charges. Otherwise null: the listing
// keeps the per-choice rules and asks its seller to set stock for each
// combination; also when it has more options than variants allow.
export function rowsFromChoiceStock(groups: OptionGroup[], priceMinor: number): VariantRow[] | null {
  const vary = variantGroups(groups);
  if (vary.length > MAX_VARIANT_GROUPS) return null;
  if (vary.length > 1 && vary.some(group => group.options.some(option => option.stock !== null))) return null;
  const combos = variantCombos(vary);
  if (!combos.length) return null;
  return combos.map(options => {
    const chosen = vary.map(group => group.options.find(option => option.name === options[group.name])!);
    const extra = chosen.reduce((sum, option) => sum + option.priceDeltaMinor, 0);
    return { id: variantId(options, vary), options, stock: vary.length === 1 ? chosen[0]!.stock : null, priceMinor: extra ? priceMinor + extra : null, photo: null };
  });
}

// What can be sold in all: the rows' total when every row has a number, and
// no more than the limit; null (no limit) when neither sets one.
export function effectiveStock(rows: VariantRow[], stockLimit: number | null): number | null {
  const total = rows.every(row => row.stock !== null) ? rows.reduce((sum, row) => sum + row.stock!, 0) : null;
  if (total === null) return stockLimit;
  return stockLimit === null ? total : Math.min(total, stockLimit);
}

// Whether a row can sell this many, before the listing's own stock.
export function rowHasStock(row: Pick<VariantRow, 'stock'>, quantity = 1): boolean {
  return row.stock === null || row.stock >= quantity;
}

// Stored rows, checked against the listing's current options. A row whose
// option was removed (for example in the live app) is dropped; a new
// combination has none in stock until the seller sets it. Null when the
// listing has no rows yet.
export function readVariantRows(value: unknown, groups: OptionGroup[]): VariantRow[] | null {
  if (!Array.isArray(value) || !value.length) return null;
  const vary = variantGroups(groups);
  const combos = variantCombos(vary);
  if (!combos.length) return null;
  const stored = value.filter(row => row && typeof row === 'object' && row.options && typeof row.options === 'object') as Record<string, unknown>[];
  return combos.map(options => {
    const row = stored.find(item => sameOptions(item.options as VariantOptions, options, vary));
    const stock = row && row.stock === null ? null : wholeNumber(row?.stock, 0, MAX_STOCK) ?? 0;
    const priceMinor = wholeNumber(row?.priceMinor, 1, MAX_PRICE_MINOR);
    const photo = typeof row?.photo === 'string' && row.photo ? row.photo : null;
    return { id: variantId(options, vary), options, stock, priceMinor, photo };
  });
}

// The row a buyer picked, once every variant group has a choice. `pickOf`
// gives the chosen option of a group (the app and checkout hold selections
// in different shapes).
export function findVariant(rows: VariantRow[] | null, groups: OptionGroup[], pickOf: (group: string) => string | undefined): VariantRow | null {
  if (!rows) return null;
  const vary = variantGroups(groups);
  const picks: VariantOptions = {};
  for (const group of vary) {
    const pick = pickOf(group.name);
    if (!pick) return null;
    picks[group.name] = pick;
  }
  return rows.find(row => sameOptions(row.options, picks, vary)) ?? null;
}

export function variantPrice(row: Pick<VariantRow, 'priceMinor'>, listingPriceMinor: number): number {
  return row.priceMinor ?? listingPriceMinor;
}

// The row's own photo, else the photo of one of its options (first group
// first), else the listing's first photo.
export function variantPhoto(row: Pick<VariantRow, 'options' | 'photo'>, groups: OptionGroup[], photos: string[]): string | null {
  if (row.photo) return row.photo;
  for (const group of variantGroups(groups)) {
    const photo = group.options.find(option => option.name === row.options[group.name])?.photo;
    if (photo) return photo;
  }
  return photos[0] ?? null;
}

// Whether a choice can be picked with the buyer's other choices: at least one
// row with it, matching what is already picked in the other groups, has stock.
// Choices that cannot are greyed out, not hidden.
export function variantOptionOpen(rows: VariantRow[] | null, groups: OptionGroup[], picked: Record<string, string[] | undefined>, group: string, option: string): boolean {
  const vary = variantGroups(groups);
  if (!rows || !vary.some(item => item.name === group)) return true;
  return rows.some(row => rowHasStock(row) && row.options[group] === option && vary.every(other => (
    other.name === group || !picked[other.name]?.length || picked[other.name]!.includes(row.options[other.name]!)
  )));
}

// What the live app reads, kept in step with the rows: each variant choice's
// stock is the sum of its rows (no limit if one of them has none), and its extra is what its cheapest row costs
// above the listing price in the first variant group (0 in the others; the
// Worker charges the row's own price either way).
export function legacyVariantStock(groups: OptionGroup[], rows: VariantRow[] | null, listingPriceMinor: number): OptionGroup[] {
  if (!rows) return groups;
  const vary = variantGroups(groups);
  return groups.map(group => {
    const at = vary.indexOf(group);
    if (at < 0) return group;
    return {
      ...group,
      options: group.options.map(option => {
        const mine = rows.filter(row => row.options[group.name] === option.name);
        const cheapest = mine.length ? Math.min(...mine.map(row => variantPrice(row, listingPriceMinor))) : listingPriceMinor;
        const stock = mine.some(row => row.stock === null) ? null : mine.reduce((sum, row) => sum + row.stock!, 0);
        return { ...option, stock, priceDeltaMinor: at === 0 ? Math.max(0, cheapest - listingPriceMinor) : 0 };
      }),
    };
  });
}

// A choice's own photo (Red's), or null.
export function optionPhotoFor(groups: OptionGroup[], group: string, option: string): string | null {
  return groups.find(item => item.name === group)?.options.find(item => item.name === option)?.photo ?? null;
}

// The live app's photo links: one entry per photo, "Group::Option|…". Options
// only: the live app swaps the main photo for a linked choice, and an extra's
// photo is a thumbnail beside it, never the main photo.
export function legacyImageVariantLinks(groups: OptionGroup[], photos: string[]): string[] {
  return photos.map(url => variantGroups(groups).flatMap(group => group.options.filter(option => option.photo === url).map(option => `${group.name}::${option.name}`)).join('|'));
}

// Option photos as stored: { Colour: { Red: url } }. Older listings only have
// the live app's links, read the same way.
export function readOptionPhotos(product: Record<string, unknown>, photos: string[]): (group: string, option: string) => string | null {
  const stored = product.optionPhotos && typeof product.optionPhotos === 'object' ? product.optionPhotos as Record<string, Record<string, unknown>> : null;
  const images = Array.isArray(product.images) ? product.images.map(String) : [];
  const links = Array.isArray(product.imageVariantLinks) ? product.imageVariantLinks : [];
  return (group, option) => {
    const own = stored?.[group]?.[option];
    if (typeof own === 'string' && photos.includes(own)) return own;
    if (stored) return null;
    const at = links.findIndex(link => typeof link === 'string' && link.split('|').includes(`${group}::${option}`));
    const url = at >= 0 ? images[at] : undefined;
    return url && photos.includes(url) ? url : null;
  };
}

export function optionPhotoMap(groups: OptionGroup[]): Record<string, Record<string, string>> {
  return Object.fromEntries(groups.map(group => [group.name, Object.fromEntries(group.options.filter(option => option.photo).map(option => [option.name, option.photo!]))]).filter(([, photos]) => Object.keys(photos as object).length));
}
